import { appSetupElements, wireAppSetup } from "./app-setup.js";
import {
  api,
  assertPasskey,
  assertWithOptions,
  browserSupportsWebAuthnAutofill,
  busy,
  followRedirect,
  pageData,
  registerPasskey,
  status,
} from "./shared.js";

type Data = {
  oauthQuery: string;
  stepUp: boolean;
  next: string;
  /** False only when this client lets an account go on with no factor. */
  requireStrongFactor?: boolean;
};

const data = pageData<Data>();
const copy = data.copy;

const emailForm = document.getElementById("email-form") as HTMLFormElement | null;
const emailInput = document.getElementById("email") as HTMLInputElement | null;
const submit = document.getElementById("submit") as HTMLButtonElement | null;
const sendCode = document.getElementById("send-code") as HTMLButtonElement | null;
const otpForm = document.getElementById("otp-form") as HTMLFormElement | null;
const otpInput = document.getElementById("otp") as HTMLInputElement | null;
const otpSubmit = document.getElementById("otp-submit") as HTMLButtonElement | null;
const otpResend = document.getElementById("otp-resend") as HTMLButtonElement | null;
const otpSent = document.getElementById("otp-sent") as HTMLParagraphElement | null;
const offer = document.getElementById("passkey-offer") as HTMLDivElement | null;
const offerAdd = document.getElementById("passkey-add") as HTMLButtonElement | null;
const existing = document.getElementById("passkey-existing") as HTMLDivElement | null;
const existingContinue = document.getElementById("passkey-existing-continue") as HTMLButtonElement | null;
const offerApp = document.getElementById("passkey-app") as HTMLButtonElement | null;
// Rendered only for a client that does not ask for a strong factor.
const offerSkip = document.getElementById("passkey-skip") as HTMLButtonElement | null;
const appCodeForm = document.getElementById("app-code-form") as HTMLFormElement | null;
const appCodeInput = document.getElementById("app-code") as HTMLInputElement | null;
const appCodeSubmit = document.getElementById("app-code-submit") as HTMLButtonElement | null;
const appCodeBackup = document.getElementById("app-code-backup") as HTMLButtonElement | null;
const subtitle = document.getElementById("subtitle") as HTMLParagraphElement | null;
const required = document.getElementById("passkey-required") as HTMLDivElement | null;
const requiredMessage = document.getElementById("passkey-required-message") as HTMLParagraphElement | null;
const requiredUse = document.getElementById("passkey-required-use") as HTMLButtonElement | null;
const requiredApp = document.getElementById("passkey-required-app") as HTMLButtonElement | null;

/**
 * Whether the address in the field holds an authenticator app, as of the last
 * answer from `/signin/challenge`. Remembered because the moment it matters is
 * after a passkey has failed, which is further on than the answer arrives.
 */
let accountHasApp = false;

const afterSignIn = () =>
  data.oauthQuery ? `/authorize?${data.oauthQuery}` : data.next || "/account";

function email(): string {
  return (emailInput?.value ?? "").trim().toLowerCase();
}

/**
 * Takes the card's opening line off screen.
 *
 * The subtitle is "Use your passkey, or we can email you a code." Every state
 * below either withdraws that offer (the passkey-required refusal: the code is
 * not a way into this account) or has moved past it (the enrolment step, the
 * app code, the existing-passkey step: this person is already through the
 * door). Leaving it up puts two statements on screen that contradict each
 * other, which is the thing `BRAND-STATE-02` exists to stop, and on the
 * refusal it offers the code in the same breath the sentence under it takes
 * the code away.
 *
 * The h1 stays. "Sign in to Acme" is still true on every one of these states,
 * and each block carries its own h2 under it saying which step this is.
 */
function hideSubtitle(): void {
  if (subtitle) subtitle.hidden = true;
}

/**
 * What the page does when a ceremony completed and the server said no.
 *
 * The failure this exists for was silent: on login.human.tech the browser
 * offered a passkey registered against login-staging (same relying-party id, so
 * the browser was right to offer it), the server refused a credential it does
 * not hold, and the page did nothing at all. The person was left looking at a
 * field that had just rejected the only credential they were offered, with no
 * sentence and nothing to press.
 *
 * So: the sentence, the reference id under it, and the way out revealed with
 * their address already in the field. `passkey_unknown_here` is the case worth
 * naming, because "that passkey could not be used" would send them to try the
 * same one again.
 */
function refused(result: {
  message?: string;
  code?: string;
  requestId?: string;
  data?: Record<string, unknown> | null;
}): void {
  const sentence =
    result.code === "passkey_unknown_here"
      ? (result.message ?? copy.passkeyUnknownHere!)
      : (result.message ?? copy.passkeyFailed!);
  status(sentence, "error", result.requestId);
  offerCode(result);
}

/**
 * Reveals the emailed-code route, with the address already filled in.
 *
 * A conditional assertion can be refused before the person has typed anything,
 * so the address is taken from the server's answer when it has one and from the
 * field otherwise. With neither, the field is focused rather than left for them
 * to find.
 */
function offerCode(result: { data?: Record<string, unknown> | null }): void {
  if (emailForm) emailForm.hidden = false;
  const returned = result.data && typeof result.data.email === "string" ? result.data.email : "";
  if (emailInput && !emailInput.value && returned) emailInput.value = returned;
  if (sendCode) sendCode.hidden = false;
  if (emailInput && !emailInput.value) emailInput.focus();
}

/**
 * The account holds a passkey, so an emailed code is not a way in (plan A2).
 *
 * The server refuses `/sign-in/email-otp` for these accounts; this is the page
 * agreeing with it. The code form goes away, because leaving a form on screen
 * that is going to be refused is how a person ends up typing six digits to
 * learn nothing. What replaces it is the sentence and one button, and the
 * button matters: the passkey may be on a phone, and a hybrid ceremony signs
 * this laptop in from that phone.
 */
function showPasskeyRequired(message?: string): void {
  hideSubtitle();
  if (emailForm) emailForm.hidden = true;
  if (otpForm) otpForm.hidden = true;
  if (offer) offer.hidden = true;
  if (existing) existing.hidden = true;
  if (message && requiredMessage) requiredMessage.textContent = message;
  // The way through for a device this person's passkey does not work on. Hidden
  // unless the account really holds an app, so the button is never one that
  // leads to a code nobody can produce.
  if (requiredApp) requiredApp.hidden = !accountHasApp;
  if (required) required.hidden = false;
  requiredUse?.focus();
}

/**
 * Asks whether this address holds a passkey, and returns the options to assert
 * it with when it does.
 *
 * Answers `null` for "no passkey, or we could not ask", which is the same
 * answer for both on purpose: a challenge route that is down must not become a
 * sign-in that cannot happen, and the server refuses a code for a passkey
 * account whatever the page believes.
 */
async function passkeyChallenge(): Promise<Record<string, unknown> | null> {
  try {
    const challenge = await api("/signin/challenge", {
      method: "POST",
      body: JSON.stringify({ email: email() }),
    });
    if (!challenge.ok || challenge.data?.passkey !== true) {
      accountHasApp = false;
      return null;
    }
    accountHasApp = challenge.data.app === true;
    return challenge.data.options as Record<string, unknown>;
  } catch {
    accountHasApp = false;
    return null;
  }
}

/**
 * A refusal on an account we already know holds a passkey.
 *
 * Different from `refused` above in one way, and it is the way that matters:
 * it does not reveal the emailed-code route, because for this account the code
 * is refused too. The sentence says what went wrong, the block says what to do
 * instead.
 */
function refusedOnPasskeyAccount(result: { message?: string; code?: string; requestId?: string }): void {
  const sentence =
    result.code === "passkey_unknown_here"
      ? (result.message ?? copy.passkeyUnknownHere!)
      : (result.message ?? copy.passkeyFailed!);
  status(sentence, "error", result.requestId);
  showPasskeyRequired();
}

/** Runs the modal assertion for the address in the field. */
async function usePasskey(button: HTMLButtonElement | null): Promise<void> {
  if (!email().includes("@")) {
    if (emailForm) emailForm.hidden = false;
    status(copy.badEmail!, "error");
    emailInput?.focus();
    return;
  }
  busy(button, true, copy.working);
  status("");
  try {
    const options = await passkeyChallenge();
    if (!options) {
      status(copy.generic!, "error");
      return;
    }
    const result = await assertWithOptions(options, data.oauthQuery);
    if (result.ok) {
      followRedirect(result.data, afterSignIn());
      return;
    }
    // A dismissed prompt leaves the block exactly as it was, with the button
    // still there to press. Nothing failed, so nothing is said.
    if (!result.cancelled) refusedOnPasskeyAccount(result);
  } finally {
    busy(button, false);
  }
}

/**
 * Conditional mediation: the browser offers the person's passkey inside the
 * email field's own dropdown, so a returning person never presses anything
 * labelled "passkey" (plan A1).
 *
 * Started on load and left running. A person who ignores it and types their
 * address instead is not interrupted, and the abort below is what stops the two
 * ceremonies fighting over the authenticator when they press the button.
 */
const autofill = new AbortController();
async function startAutofill(): Promise<void> {
  if (!(await browserSupportsWebAuthnAutofill())) return;
  try {
    const result = await assertPasskey({
      autoFill: true,
      oauthQuery: data.oauthQuery,
      signal: autofill.signal,
    });
    if (result.ok) {
      followRedirect(result.data, afterSignIn());
      return;
    }
    // A dismissed or aborted conditional request is the normal case: the person
    // chose to type instead, and a message would put an error on a page where
    // nothing went wrong. A *refusal* is not that. The browser offered a
    // credential, the person picked it, and the server said no; staying quiet
    // there is the bug this page shipped with.
    if (!result.cancelled) refused(result);
  } catch {
    // Nothing above throws any more. Kept because a future change to the
    // helpers must not turn a throw into a silent page again.
  }
}

emailForm?.addEventListener("submit", async (event) => {
  event.preventDefault();
  if (!email().includes("@")) {
    status(copy.badEmail!, "error");
    emailInput?.focus();
    return;
  }
  autofill.abort();
  busy(submit, true, copy.working);
  status("");
  try {
    // A step-up is always a passkey and never a code, so it takes the direct
    // route: the person is already signed in and the browser already knows
    // which credentials apply.
    if (data.stepUp) {
      const result = await assertPasskey({ oauthQuery: data.oauthQuery, stepUp: true });
      if (result.ok) {
        followRedirect(result.data, afterSignIn());
      } else if (result.cancelled) {
        status(copy.stepUpNeedsPasskey!, "error");
      } else {
        // No code route here by design, so the sentence is all there is: it had
        // better be the specific one.
        status(result.message ?? copy.stepUpNeedsPasskey!, "error", result.requestId);
      }
      return;
    }

    /**
     * The person has told us who they are, so ask whether that account has a
     * passkey and, if it does, run the ceremony naming its credentials. This is
     * the route for someone whose browser never offered autofill: without it
     * they own a passkey the page simply never uses.
     *
     * Still no button labelled "sign in with a passkey" (A1). This happens
     * behind Continue, after the address is known.
     */
    const options = await passkeyChallenge();

    if (options) {
      const result = await assertWithOptions(options, data.oauthQuery);
      if (result.ok) {
        followRedirect(result.data, afterSignIn());
        return;
      }
      // Refused after the ceremony: say which refusal it was. A person whose
      // browser offered the wrong deployment's passkey should be told that,
      // not handed a mailbox to go and check.
      if (!result.cancelled) {
        refusedOnPasskeyAccount(result);
        return;
      }
      /**
       * Cancelled, or nothing on this device matched. This used to fall
       * through to an emailed code, and that is exactly the route A2 closes:
       * the account holds a passkey, so the server refuses the code. Offering
       * it would mean a person types six digits to be told no. The block says
       * so, and its button runs the ceremony again, which is what a person on
       * a laptop whose passkey is on their phone needs.
       */
      showPasskeyRequired();
      return;
    }

    await requestCode();
  } catch {
    await requestCode();
  } finally {
    busy(submit, false);
  }
});

sendCode?.addEventListener("click", async () => {
  if (!email().includes("@")) {
    status(copy.badEmail!, "error");
    emailInput?.focus();
    return;
  }
  autofill.abort();
  busy(sendCode, true, copy.working);
  let held: Record<string, unknown> | null = null;
  try {
    held = await passkeyChallenge();
  } finally {
    busy(sendCode, false);
  }
  // Asking first rather than sending and refusing afterwards. The server
  // refuses either way; sending would post a code to a mailbox that cannot use
  // it, which is a mail nobody asked for and a minute of somebody's day.
  //
  // Unless the account holds an authenticator app, and then the server does not
  // refuse: the code is the first of two steps and the app's code is the
  // second. Withdrawing it here was the page being stricter than the server,
  // and it left somebody who had set up an app with no way to use it.
  if (held && !accountHasApp) {
    showPasskeyRequired();
    return;
  }
  await requestCode();
});

otpResend?.addEventListener("click", () => void requestCode());

async function requestCode(): Promise<void> {
  busy(sendCode, true, copy.working);
  status("");
  try {
    const result = await api("/api/auth/email-otp/send-verification-otp", {
      method: "POST",
      body: JSON.stringify({ email: email(), type: "sign-in", oauth_query: data.oauthQuery }),
    });
    if (!result.ok) {
      status(result.message ?? copy.generic!, "error", result.requestId);
      return;
    }
    if (emailForm) emailForm.hidden = true;
    if (otpForm) otpForm.hidden = false;
    if (otpSent) otpSent.textContent = (copy.otpSentTemplate ?? "").replace("{email}", email());
    otpInput?.focus();
  } finally {
    busy(sendCode, false);
  }
}

otpForm?.addEventListener("submit", async (event) => {
  event.preventDefault();
  busy(otpSubmit, true, copy.working);
  status("");
  try {
    const result = await api("/api/auth/sign-in/email-otp", {
      method: "POST",
      body: JSON.stringify({
        email: email(),
        otp: (otpInput?.value ?? "").trim(),
        oauth_query: data.oauthQuery,
      }),
    });
    if (!result.ok) {
      /**
       * The account holds a passkey, so the code was never going to open it
       * (plan A2). The page cannot know that before it asks: a code can be
       * requested from anywhere, and the account may have gained a passkey
       * since. So this is where the page finds out, and it says the server's
       * own sentence rather than a paraphrase of it.
       */
      if (result.code === "PASSKEY_REQUIRED") {
        status("");
        showPasskeyRequired(result.message ?? copy.passkeyRequiredToSignIn);
        return;
      }
      status(result.message ?? copy.generic!, "error", result.requestId);
      return;
    }
    /**
     * The account has an authenticator app, so the emailed code was the first
     * of two steps and no session exists yet (#386). The server says so with
     * the two-factor plugin's own answer; what follows is its own endpoint.
     */
    if (result.data?.twoFactorRedirect === true) {
      hideSubtitle();
      if (otpForm) otpForm.hidden = true;
      if (appCodeForm) appCodeForm.hidden = false;
      appCodeInput?.focus();
      return;
    }

    pendingRedirect = typeof result.data?.url === "string" ? result.data.url : afterSignIn();
    if (otpForm) otpForm.hidden = true;

    /**
     * Offer to put a passkey on this device (plan A2), but only when this
     * account has none.
     *
     * A code proves the mailbox, and an account that a passkey already protects
     * cannot have its passkeys changed by a mailbox: the server refuses it, and
     * showing a button that is going to be refused is worse than not showing
     * it. Those people are told to use the passkey they have, which works from
     * another device over hybrid.
     *
     * Since A2 the count is one on a code sign-in that succeeded, because a
     * code sign-in for an account that holds a passkey does not succeed. The
     * other branch is kept anyway: if that ever stops being true, the honest
     * page is still one sentence away rather than one release away.
     */
    const held = await api("/api/auth/passkey/list-user-passkeys");
    const count = Array.isArray(held.data) ? held.data.length : 0;
    if (count === 0) {
      hideSubtitle();
      if (offer) offer.hidden = false;
    } else if (existing) {
      hideSubtitle();
      existing.hidden = false;
    } else {
      window.location.assign(pendingRedirect);
    }
    // "Not now" exists only when the server rendered it, which it does only
    // for a client whose workspace turned the requirement off
    // (internal-docs#3720). For every other client an account whose only key
    // is a mailbox is the thing the rule exists to prevent, and `/authorize`
    // refuses to hand a product session to one, so a skip would only move the
    // dead end.
  } finally {
    busy(otpSubmit, false);
  }
});

let pendingRedirect = "/account";

/**
 * Where to go once a factor has just been enrolled.
 *
 * **Not** the callback url the sign-in handed back, and the reason is the
 * ceremony that has happened since. Confirming an authenticator app mints a new
 * session and deletes the one the code made (Better Auth's own two-factor
 * plugin does the swap), and the authorization code in that url was issued
 * against the old one. Following it lands the product on an exchange for a
 * session that no longer exists, which is a sign-in that ends on an error page
 * with nothing wrong.
 *
 * So the authorization is walked again instead. There is a live session by now,
 * so it is silent: one redirect, a fresh code, and the product gets a session
 * minted from the account as it *is* — which is also the account the enrolment
 * gate is about to be asked about.
 */
const afterEnrolment = (): string => (data.oauthQuery ? afterSignIn() : pendingRedirect);

offerAdd?.addEventListener("click", async () => {
  busy(offerAdd, true, copy.working);
  try {
    const result = await registerPasskey({});
    if (!result.ok) status(result.message ?? copy.passkeyFailed!, "error", result.requestId);
  } catch {
    status(copy.passkeyFailed!, "error");
  } finally {
    busy(offerAdd, false);
    window.location.assign(afterEnrolment());
  }
});

/** Set once the person says the app is gone and they want a backup code. */
let usingBackupCode = false;

const appSetup = wireAppSetup({
  el: appSetupElements(),
  copy,
  hide: [offer].filter((n): n is HTMLDivElement => Boolean(n)),
  onDone: () => window.location.assign(afterEnrolment()),
});

offerApp?.addEventListener("click", () => void appSetup.start());

/**
 * Leaving the passkey behind: the emailed code, then the app's code. Reached
 * from the refusal block, which is where a person lands when their passkey is
 * not usable here — the device this app was set up for.
 */
requiredApp?.addEventListener("click", () => {
  hideSubtitle();
  if (required) required.hidden = true;
  status("");
  void requestCode();
});

/**
 * The second step of a sign-in that began with an emailed code.
 *
 * Straight to the plugin's endpoint: it holds the half-finished sign-in in a
 * short-lived cookie, counts the attempts, and mints the session when the code
 * is right. The page's job is the sentence and the way to a backup code.
 */
appCodeForm?.addEventListener("submit", async (event) => {
  event.preventDefault();
  const code = (appCodeInput?.value ?? "").trim();
  busy(appCodeSubmit, true, copy.working);
  status("");
  try {
    const path = usingBackupCode
      ? "/api/auth/two-factor/verify-backup-code"
      : "/api/auth/two-factor/verify-totp";
    const result = await api(path, { method: "POST", body: JSON.stringify({ code }) });
    if (!result.ok) {
      status(result.message ?? copy.appCodeWrong!, "error", result.requestId);
      appCodeInput?.select();
      return;
    }
    followRedirect(result.data, afterSignIn());
  } finally {
    busy(appCodeSubmit, false);
  }
});

/**
 * A backup code instead. Same field, because it is the same question asked of
 * somebody who no longer has the app: the label and the input's shape change,
 * and the endpoint changes with them.
 */
appCodeBackup?.addEventListener("click", () => {
  usingBackupCode = true;
  if (appCodeInput) {
    appCodeInput.value = "";
    appCodeInput.removeAttribute("pattern");
    appCodeInput.removeAttribute("maxlength");
    appCodeInput.setAttribute("inputmode", "text");
    const label = appCodeForm?.querySelector('label[for="app-code"]');
    if (label && copy.appCodeBackupLabel) label.textContent = copy.appCodeBackupLabel;
    appCodeInput.focus();
  }
  if (appCodeBackup) appCodeBackup.hidden = true;
});
existingContinue?.addEventListener("click", () => window.location.assign(pendingRedirect));
// Nothing was enrolled, so no session swap happened and the callback url the
// code sign-in returned is still good, as it is for `existingContinue`.
offerSkip?.addEventListener("click", () => window.location.assign(pendingRedirect));
requiredUse?.addEventListener("click", () => void usePasskey(requiredUse));

// Step-up never offers the code, so it never offers autofill's alternative
// either: the passkey is the only route and pressing the button starts it.
if (!data.stepUp) void startAutofill();
